import { Add } from '@mui/icons-material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Box,
  Button,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ManufacturerDraft } from './ManufacturerList.types'
import { useCatalogData } from '../CatalogList/useCatalogData'

export interface ManufacturerModalProps {
  open: boolean
  onClose: () => void
  onSave: (draft: ManufacturerDraft) => void
}

export function ManufacturerModal({ open, onClose, onSave }: ManufacturerModalProps) {
  const [draft, setDraft] = useState<ManufacturerDraft>({
    supplier: '',
    alias: '',
    mfrCodeUpper: '',
    url: '',
    direct: false,
    contractAliases: [{ contractNumber: '', alias: '' }],
  })
  const codeEdited = useRef(false)

  const fetchSuggestedCode = (name: string) => {
    if (codeEdited.current) return
    if (!name.trim()) {
      setDraft((p: ManufacturerDraft) => ({ ...p, mfrCodeUpper: '' }))
      return
    }
    axios
      .get<{ code: string | null }>('/manufacturer/suggest-code', { params: { name } })
      .then(res => {
        if (codeEdited.current) return
        setDraft((p: ManufacturerDraft) => ({ ...p, mfrCodeUpper: res.data.code ?? '' }))
      })
      .catch(err => {
        console.error(err)
      })
  }
  const debouncedFetchSuggestedCode = useRef(debounce(fetchSuggestedCode, 500)).current

  useEffect(() => {
    if (!open) return
    codeEdited.current = false
    setDraft({
      supplier: '',
      alias: '',
      mfrCodeUpper: '',
      url: '',
      direct: false,
      contractAliases: [{ contractNumber: '', alias: '' }],
    })
  }, [open])

  const canSave = draft.supplier.trim().length > 0 && draft.mfrCodeUpper.trim().length > 0

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Manufacturer</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          Fields marked with a * are required.
        </Typography>
        <Box display="flex" flexDirection="column" gap={2} paddingTop={1}>
          <TextField
            label="Manufacturer *"
            size="small"
            value={draft.supplier}
            onChange={e => {
              setDraft((p: ManufacturerDraft) => ({ ...p, supplier: e.target.value }))
              debouncedFetchSuggestedCode(e.target.value)
            }}
            fullWidth
          />
          <TextField
            label="Alias"
            size="small"
            value={draft.alias}
            onChange={e => setDraft((p: ManufacturerDraft) => ({ ...p, alias: e.target.value }))}
            fullWidth
          />
          <ContractAliasesEditor
            value={draft.contractAliases}
            onChange={next => setDraft((p: ManufacturerDraft) => ({ ...p, contractAliases: next }))}
            size="small"
          />
          <TextField
            label="OEM Code *"
            size="small"
            value={draft.mfrCodeUpper}
            onChange={e => {
              codeEdited.current = e.target.value !== ''
              setDraft((p: ManufacturerDraft) => ({ ...p, mfrCodeUpper: e.target.value.toUpperCase() }))
            }}
            fullWidth
          />
          <TextField
            label="URL"
            size="small"
            value={draft.url}
            onChange={e => setDraft((p: ManufacturerDraft) => ({ ...p, url: e.target.value }))}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => onSave(draft)}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function ContractAliasesEditor({
  value,
  onChange,
  disabled = false,
  size,
}: {
  value: { contractNumber: string; alias: string }[]
  onChange: (next: { contractNumber: string; alias: string }[]) => void
  disabled?: boolean
  size?: 'small' | 'medium'
}) {
  const { rows: catalogs } = useCatalogData()
  const contracts = useMemo(
    () =>
      catalogs
        .filter(contract => contract.status === 'Active' && contract.parentId === null)
        .sort((a, b) => a.contractVehicle.localeCompare(b.contractVehicle)),
    [catalogs],
  )

  const updateRow = (index: number, patch: Partial<{ contractNumber: string; alias: string }>) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)))

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {value.map((row, index) => (
        <Box key={index} display="flex" gap={1} alignItems="flex-start">
          <Box minWidth={180}>
            <TextField
              select
              label="Contract"
              value={row.contractNumber}
              onChange={e => updateRow(index, { contractNumber: e.target.value })}
              disabled={disabled}
              size={size}
              fullWidth
            >
              {contracts.map(contract => (
                <MenuItem key={contract.id} value={contract.contractNumber.toLowerCase()}>
                  {contract.contractVehicle} ({contract.contractNumber})
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField
            label="Contract Alias"
            value={row.alias}
            onChange={e => updateRow(index, { alias: e.target.value })}
            disabled={disabled}
            size={size}
            fullWidth
            helperText="Must match the contract's manufacturer list exactly"
          />
          <IconButton
            size="small"
            aria-label="Delete contract alias row"
            onClick={() => onChange(value.filter((_, i) => i !== index))}
            disabled={disabled}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      ))}
      <Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={() => onChange([...value, { contractNumber: '', alias: '' }])}
          disabled={disabled}
        >
          ADD CONTRACT ALIAS
        </Button>
      </Box>
    </Box>
  )
}
